import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Search, Filter, Calendar, MapPin, DollarSign, ArrowRight, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Emenda } from '../admin/types/emenda';
import { cn } from '../lib/utils';

export const Amendments = () => {
  const [amendments, setAmendments] = useState<Emenda[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [selectedYear, setSelectedYear] = useState('Todos');

  useEffect(() => {
    fetchAmendments();
  }, []);

  const fetchAmendments = async () => {
    try {
      const { data, error } = await supabase
        .from('emendas')
        .select('*')
        .order('data_publicacao', { ascending: false });
      
      if (error) throw error;
      setAmendments(data || []);
    } catch (err) {
      console.error('Error fetching amendments:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const cats = new Set(amendments.map(a => a.area_investimento));
    return ['Todas', ...Array.from(cats)].filter(Boolean);
  }, [amendments]);

  const years = useMemo(() => {
    const yrs = new Set(amendments.map(a => {
      const date = a.data_publicacao;
      return date ? new Date(date).getFullYear().toString() : 'Desconhecido';
    }));
    return ['Todos', ...Array.from(yrs).sort((a: string, b: string) => b.localeCompare(a))];
  }, [amendments]);

  const filteredAmendments = useMemo(() => {
    return amendments.filter(amendment => {
      const title = amendment.titulo || '';
      const summary = amendment.resumo || '';
      const category = amendment.area_investimento || '';
      const year = (amendment.data_publicacao ? new Date(amendment.data_publicacao).getFullYear().toString() : '');

      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          summary.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'Todas' || category === selectedCategory;
      const matchesYear = selectedYear === 'Todos' || year === selectedYear;
      
      return matchesSearch && matchesCategory && matchesYear;
    });
  }, [searchTerm, selectedCategory, selectedYear, amendments]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Carregando emendas...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-6 py-12 min-h-screen">
      <div className="max-w-4xl mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-6xl font-black text-primary mb-6 tracking-tight">Emendas Parlamentares</h1>
          <p className="text-lg text-slate-600 leading-relaxed font-medium">
            Transparência na aplicação de recursos públicos. Acompanhe aqui o detalhamento das emendas parlamentares destinadas à nossa instituição, seus objetivos e o impacto na comunidade escolar.
          </p>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm mb-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por título ou descrição..."
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-primary/10 transition-all outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <select
              className="w-full pl-12 pr-10 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <select
              className="w-full pl-12 pr-10 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {years.map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Grid */}
      {filteredAmendments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAmendments.map((amendment, index) => {
            const title = amendment.titulo;
            const category = amendment.area_investimento;
            const year = amendment.data_publicacao ? new Date(amendment.data_publicacao).getFullYear().toString() : '';
            const value = amendment.valor;
            const municipality = amendment.municipio;
            const summary = amendment.resumo;
            const image = amendment.imagem_url;

            return (
              <motion.div
                key={amendment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col overflow-hidden group"
              >
                {image && (
                  <div className="aspect-video w-full overflow-hidden">
                    <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-8 flex-grow space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black rounded-lg uppercase tracking-widest">
                      {category}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      {year}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-black text-slate-900 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                    {title}
                  </h3>
                  
                  <p className="text-slate-500 text-sm font-medium leading-relaxed line-clamp-3">
                    {summary}
                  </p>
                  
                  <div className="pt-4 space-y-3 border-t border-slate-50">
                    <div className="flex items-center gap-3 text-slate-700">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><DollarSign size={16} /></div>
                      <span className="text-sm font-black">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <div className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center"><MapPin size={16} /></div>
                      <span className="text-sm font-bold">{municipality}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-8 pt-0 mt-auto">
                  <Link
                    to={`/emendas/${amendment.id}`}
                    className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-blue-600 transition-all group/btn shadow-lg shadow-slate-900/10 active:scale-95"
                  >
                    Ver Detalhes
                    <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300 shadow-sm">
            <Search size={32} />
          </div>
          <h3 className="text-2xl font-black text-slate-900 mb-2">Nenhuma emenda encontrada</h3>
          <p className="text-slate-500 font-medium">Tente ajustar seus filtros ou termo de busca.</p>
        </div>
      )}
    </div>
  );
};
